import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Tag } from "primereact/tag";
import { useDispatch } from "react-redux";
import {
    grantPremium,
    addCredits,
    decreaseCredits,
} from "../../redux/actions/userSubscriptionAction";
import { exportTableToCsv } from "../../utils/exportTableCsv";

const statusOptions = [
    { label: "All", value: "" },
    { label: "Premium", value: "Premium" },
    { label: "Free", value: "Free" },
    { label: "Expired", value: "Expired" },
];

const durationOptions = [
    { label: "Monthly", value: "monthly" },
    { label: "Yearly", value: "yearly" },
    { label: "Lifetime", value: "lifetime" },
];

const planTypes = [
    { label: "Android", value: "ANDROID" },
    { label: "iOS", value: "IOS" },
];

export default function UserSubscriptionTable({
    heading, data, loading, search, setSearch,
    statusFilter, setStatusFilter, refreshData,
}) {
    const dispatch = useDispatch();

    // Client-side pagination
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(50);

    // Dialog state
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogType, setDialogType] = useState(""); // "premium" | "credits" | "decreaseCredits"
    const [selectedUser, setSelectedUser] = useState(null);

    // Reset to first page when the filtered data or filters change
    useEffect(() => {
        setFirst(0);
    }, [data.length, search, statusFilter]);

    // Grant Premium form
    const [planType, setPlanType] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [duration, setDuration] = useState("lifetime");

    // Add Credits form
    const [credits, setCredits] = useState(50);
    const [expiryDays, setExpiryDays] = useState(365);

    const openPremiumDialog = (rowData) => {
        setSelectedUser(rowData);
        setDialogType("premium");
        setPlanType(null);
        setSelectedVehicle(rowData.vehicles?.[0] || null);
        setDuration("lifetime");
        setDialogVisible(true);
    };

    const openCreditsDialog = (rowData) => {
        setSelectedUser(rowData);
        setDialogType("credits");
        setCredits(50);
        setExpiryDays(365);
        setDialogVisible(true);
    };

    const openDecreaseCreditsDialog = (rowData) => {
        const remainingCalls = Number(rowData.maskedCallSubscription?.remainingCalls) || 0;
        setSelectedUser(rowData);
        setDialogType("decreaseCredits");
        setCredits(Math.min(50, remainingCalls));
        setDialogVisible(true);
    };

    const closeDialog = () => {
        setDialogVisible(false);
        setSelectedUser(null);
    };

    const handleGrantPremium = () => {
        if (!planType || !selectedVehicle) {
            alert("Please select vehicle and plan type");
            return;
        }
        dispatch(grantPremium(
            {
                userId: selectedUser._id,
                vehicleId: selectedVehicle._id,
                planType,
                duration,
            },
            null,
            () => { closeDialog(); refreshData(); }
        ));
    };

    const handleAddCredits = () => {
        if (!credits || credits <= 0) {
            alert("Please enter valid credits");
            return;
        }
        dispatch(addCredits(
            {
                userId: selectedUser._id,
                credits,
                expiryDays,
            },
            null,
            () => { closeDialog(); refreshData(); }
        ));
    };

    const handleDecreaseCredits = () => {
        const remainingCalls = Number(selectedUser?.maskedCallSubscription?.remainingCalls) || 0;
        if (!credits || credits <= 0 || credits > remainingCalls) {
            alert(`Please enter credits between 1 and ${remainingCalls}`);
            return;
        }
        dispatch(decreaseCredits(
            {
                userId: selectedUser._id,
                credits,
            },
            null,
            () => { closeDialog(); refreshData(); }
        ));
    };

    const exportAll = () => {
        const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const activeSubscription = (row) =>
            row.subscriptions?.find((s) => s.status === "active" && new Date(s.currentEnd) > new Date());

        exportTableToCsv("user-subscriptions.csv", data, [
            { header: "Name", value: (row) => [row.firstName, row.lastName].filter(Boolean).join(" ") || "-" },
            { header: "Mobile", value: "mobile" },
            { header: "Vehicle(s)", value: (row) => (row.vehicles?.length ? row.vehicles.map((v) => v.vehicleNumber).join(", ") : "-") },
            { header: "Status", value: "subscriptionStatus" },
            {
                header: "Plan",
                value: (row) => {
                    const active = activeSubscription(row);
                    if (!active) return "No Plan";
                    return active.paymentMethod === "admin_granted" || active.paymentMethod === "manual_lifetime"
                        ? "Admin Granted"
                        : active.planId || "Paid";
                },
            },
            {
                header: "Validity",
                value: (row) => {
                    const active = activeSubscription(row);
                    return active ? `${fmt(active.currentStart)} to ${fmt(active.currentEnd)}` : "-";
                },
            },
            {
                header: "Call Credits",
                value: (row) => {
                    const mc = row.maskedCallSubscription;
                    return mc ? `${mc.remainingCalls} / ${mc.totalCalls}` : "0 / 0";
                },
            },
            { header: "Device", value: "deviceType" },
        ]);
    };

    // Column templates
    const nameBody = (row) =>
        [row.firstName, row.lastName].filter(Boolean).join(" ") || "-";

    const vehiclesBody = (row) =>
        row.vehicles?.length
            ? row.vehicles.map((v) => v.vehicleNumber).join(", ")
            : "-";

    const statusBody = (row) => {
        const s = row.subscriptionStatus;
        const severity = s === "Premium" ? "success" : s === "Expired" ? "danger" : "info";
        return <Tag value={s} severity={severity} />;
    };

    const planBody = (row) => {
        const active = row.subscriptions?.find(
            (s) => s.status === "active" && new Date(s.currentEnd) > new Date()
        );
        if (!active) return <span className="text-500">No Plan</span>;
        return (
            <span>
                {active.paymentMethod === "admin_granted" || active.paymentMethod === "manual_lifetime"
                    ? "Admin Granted"
                    : active.planId || "Paid"}
            </span>
        );
    };

    const validityBody = (row) => {
        const active = row.subscriptions?.find(
            (s) => s.status === "active" && new Date(s.currentEnd) > new Date()
        );
        if (!active) return "-";
        const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        return `${fmt(active.currentStart)} → ${fmt(active.currentEnd)}`;
    };

    const creditsBody = (row) => {
        const mc = row.maskedCallSubscription;
        if (!mc) return <span className="text-500">0 / 0</span>;
        const isActive = mc.status === "active" && new Date(mc.expiresAt) > new Date();
        return (
            <span className={isActive ? "text-green-600 font-semibold" : "text-500"}>
                {mc.remainingCalls} / {mc.totalCalls}
            </span>
        );
    };

    const actionsBody = (row) => {
        const hasVehicle = row.vehicles?.length > 0;
        const maskedCallSubscription = row.maskedCallSubscription;
        const remainingCalls = Number(maskedCallSubscription?.remainingCalls) || 0;
        const canDecreaseCredits =
            maskedCallSubscription?.status === "active" &&
            new Date(maskedCallSubscription.expiresAt) > new Date() &&
            remainingCalls > 0;
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-star"
                    className="p-button-rounded p-button-success p-button-sm"
                    tooltip="Grant Premium"
                    tooltipOptions={{ position: "top" }}
                    disabled={!hasVehicle}
                    onClick={() => openPremiumDialog(row)}
                />
                <Button
                    icon="pi pi-phone"
                    className="p-button-rounded p-button-info p-button-sm"
                    tooltip="Add Call Credits"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => openCreditsDialog(row)}
                />
                <Button
                    icon="pi pi-phone"
                    className="p-button-rounded p-button-danger p-button-sm"
                    tooltip="Decrease Call Credits"
                    tooltipOptions={{ position: "top" }}
                    disabled={!canDecreaseCredits}
                    onClick={() => openDecreaseCreditsDialog(row)}
                />
            </div>
        );
    };

    const isPremiumDialog = dialogType === "premium";
    const isDecreaseCreditsDialog = dialogType === "decreaseCredits";
    const dialogActionLabel = isPremiumDialog
        ? "Grant Premium"
        : isDecreaseCreditsDialog
          ? "Decrease Credits"
          : "Add Credits";
    const handleDialogSubmit = isPremiumDialog
        ? handleGrantPremium
        : isDecreaseCreditsDialog
          ? handleDecreaseCredits
          : handleAddCredits;

    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={closeDialog} />
            <Button
                label={dialogActionLabel}
                icon="pi pi-check"
                className={isDecreaseCreditsDialog ? "p-button-danger" : ""}
                onClick={handleDialogSubmit}
                autoFocus
            />
        </div>
    );

    const userName = selectedUser
        ? [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(" ") || selectedUser.mobile
        : "";

    return (
        <>
            {/* Grant Premium / Adjust Credits Dialog */}
            <Dialog
                visible={dialogVisible}
                onHide={closeDialog}
                header={
                    isPremiumDialog
                        ? `Grant Premium — ${userName}`
                        : isDecreaseCreditsDialog
                          ? `Decrease Call Credits — ${userName}`
                          : `Add Call Credits — ${userName}`
                }
                style={{ width: "450px" }}
                footer={dialogFooter}
                draggable={false}
                dismissableMask
            >
                {isPremiumDialog ? (
                    <div className="flex flex-column gap-3">
                        <div className="field">
                            <label className="font-semibold mb-2 block">Vehicle</label>
                            <Dropdown
                                value={selectedVehicle}
                                options={selectedUser?.vehicles || []}
                                onChange={(e) => setSelectedVehicle(e.value)}
                                optionLabel="vehicleNumber"
                                placeholder="Select Vehicle"
                                className="w-full"
                            />
                        </div>
                        <div className="field">
                            <label className="font-semibold mb-2 block">Platform</label>
                            <Dropdown
                                value={planType}
                                options={planTypes}
                                onChange={(e) => setPlanType(e.value)}
                                placeholder="Select Platform"
                                className="w-full"
                            />
                        </div>
                        <div className="field">
                            <label className="font-semibold mb-2 block">Duration</label>
                            <Dropdown
                                value={duration}
                                options={durationOptions}
                                onChange={(e) => setDuration(e.value)}
                                placeholder="Select Duration"
                                className="w-full"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-column gap-3">
                        {selectedUser?.maskedCallSubscription && (
                            <div className="surface-100 border-round p-3">
                                <span className="text-500">Current Credits: </span>
                                <span className="font-bold">
                                    {selectedUser.maskedCallSubscription.remainingCalls} / {selectedUser.maskedCallSubscription.totalCalls}
                                </span>
                            </div>
                        )}
                        <div className="field">
                            <label className="font-semibold mb-2 block">
                                {isDecreaseCreditsDialog ? "Credits to Decrease" : "Credits to Add"}
                            </label>
                            <InputNumber
                                value={credits}
                                onValueChange={(e) => setCredits(e.value)}
                                min={1}
                                max={
                                    isDecreaseCreditsDialog
                                        ? Number(selectedUser?.maskedCallSubscription?.remainingCalls) || 0
                                        : 10000
                                }
                                className="w-full"
                            />
                        </div>
                        {!isDecreaseCreditsDialog && (
                            <div className="field">
                                <label className="font-semibold mb-2 block">Expiry (days, 0 = lifetime)</label>
                                <InputNumber
                                    value={expiryDays}
                                    onValueChange={(e) => setExpiryDays(e.value)}
                                    min={0}
                                    max={3650}
                                    className="w-full"
                                />
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Main Table */}
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                        <h5 className="m-0">{heading}</h5>
                        <div className="flex gap-2 align-items-center">
                            <Dropdown
                                value={statusFilter}
                                options={statusOptions}
                                onChange={(e) => setStatusFilter(e.value)}
                                placeholder="Filter Status"
                                className="w-10rem"
                            />
                            <span className="p-input-icon-left">
                                <i className="pi pi-search" />
                                <InputText
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search name or vehicle..."
                                />
                            </span>
                            <Button
                                label="Export CSV"
                                icon="pi pi-download"
                                onClick={exportAll}
                                disabled={!data.length}
                                severity="success"
                            />
                        </div>
                    </div>

                    <DataTable
                        value={data}
                        paginator
                        rows={rows}
                        first={first}
                        onPage={(event) => {
                            setFirst(event.first);
                            setRows(event.rows);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        loading={loading}
                        emptyMessage="No users found."
                        className="custom-datatable-header"
                        showGridlines
                    >
                        <Column
                            body={nameBody}
                            header="Name"
                            sortable
                            sortField="firstName"
                        />
                        <Column field="mobile" header="Mobile" sortable />
                        <Column body={vehiclesBody} header="Vehicle(s)" />
                        <Column body={statusBody} header="Status" style={{ width: "100px" }} />
                        <Column body={planBody} header="Plan" />
                        <Column body={validityBody} header="Validity" />
                        <Column body={creditsBody} header="Call Credits" style={{ width: "120px" }} />
                        <Column field="deviceType" header="Device" sortable style={{ width: "90px" }} />
                        <Column body={actionsBody} header="Actions" style={{ width: "155px" }} />
                    </DataTable>
                </div>
            </div>
        </>
    );
}
